/**
 * Templates → Abbreviations: the firm's DRAWING ABBREVIATIONS (docs/bim-plan/28 round 7). The
 * AlderCAD G001 title sheet prints the ones a set actually uses (abbreviationsUsed.js), in
 * alphabetical order — G003 is retired (2026-10-19). Each row: the letters, the meaning (edit, saves on leaving the box), and
 * whether it prints. Add a row at the bottom; remove one with ×. Changes show the next time a
 * model is opened.
 */
import { useEffect, useMemo, useState } from 'react'
import { listDrawingAbbreviations, saveDrawingAbbreviation, addDrawingAbbreviation, removeDrawingAbbreviation } from '../services/drawingAbbreviations.js'
import { confirmDialog } from './Dialog.jsx'

const FONT = 'DM Sans, sans-serif'
const MONO = 'DM Mono, monospace'
const input = { fontFamily: FONT, fontSize: 12, padding: '5px 7px', borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' }

function AbbrRow({ r, onSaved, onRemoved, addToast }) {
  const [abbr, setAbbr] = useState(r.abbr)
  const [meaning, setMeaning] = useState(r.meaning)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setAbbr(r.abbr); setMeaning(r.meaning) }, [r.abbr, r.meaning])

  async function commit(patch) {
    setSaving(true)
    try {
      await saveDrawingAbbreviation(r.id, patch)
      onSaved({ ...r, ...patch })
    } catch (e) {
      addToast('Save failed: ' + (e.message || 'unknown error'))
      setAbbr(r.abbr); setMeaning(r.meaning)
    } finally { setSaving(false) }
  }
  async function remove() {
    if (!(await confirmDialog({ title: 'Remove abbreviation?', message: `Remove ${r.abbr} (${r.meaning}) from the list?`, confirmLabel: 'Remove', danger: true }))) return
    try { await removeDrawingAbbreviation(r.id); onRemoved(r.id) } catch (e) { addToast('Remove failed: ' + (e.message || 'unknown error')) }
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 70px 28px', gap: 10, alignItems: 'center', padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)', opacity: r.active ? 1 : 0.55 }}>
      <input value={abbr} disabled={saving} onChange={(e) => setAbbr(e.target.value)}
        onBlur={() => { const a = abbr.trim(); if (a && a !== r.abbr) commit({ abbr: a }); else setAbbr(r.abbr) }}
        style={{ ...input, fontFamily: MONO, fontWeight: 600 }} />
      <input value={meaning} disabled={saving} onChange={(e) => setMeaning(e.target.value)}
        onBlur={() => { const m = meaning.trim(); if (m && m !== r.meaning) commit({ meaning: m }); else setMeaning(r.meaning) }}
        style={input} />
      <label style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-mid)', display: 'flex', alignItems: 'center', gap: 5 }}
        title="Unchecked: never printed on G001">
        <input type="checkbox" checked={r.active} disabled={saving} onChange={(e) => commit({ active: e.target.checked })} />
        Prints
      </label>
      <button onClick={remove} title="Remove" style={{ border: 'none', background: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 15 }}>×</button>
    </div>
  )
}

export default function DrawingAbbreviationsList({ addToast }) {
  const [list, setList] = useState(null)   // null = loading
  const [newAbbr, setNewAbbr] = useState('')
  const [newMeaning, setNewMeaning] = useState('')
  useEffect(() => { listDrawingAbbreviations().then(setList).catch((e) => { addToast('Could not load abbreviations: ' + e.message); setList([]) }) }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  const sorted = useMemo(() => (list || []).slice().sort((a, b) => a.abbr.replace(/[^A-Z0-9]/gi, '').localeCompare(b.abbr.replace(/[^A-Z0-9]/gi, ''))), [list])

  async function add() {
    const a = newAbbr.trim(), m = newMeaning.trim()
    if (!a || !m) return
    try {
      const row = await addDrawingAbbreviation(a.toUpperCase(), m.toUpperCase())
      setList((l) => [...(l || []), row])
      setNewAbbr(''); setNewMeaning('')
    } catch (e) { addToast('Add failed: ' + (e.message || 'unknown error')) }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 24px 24px' }}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)', margin: '2px 0 12px', maxWidth: 780, lineHeight: 1.5 }}>
        Drawing abbreviations. Each project's G001 title sheet prints the ones its drawings and notes actually use, in alphabetical order. Changes show the next time a model is opened.
      </div>
      {list === null ? (
        <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading…</div>
      ) : (
        <div style={{ maxWidth: 780 }}>
          {sorted.map((r) => (
            <AbbrRow key={r.id} r={r} addToast={addToast}
              onSaved={(next) => setList((l) => l.map((x) => (x.id === next.id ? next : x)))}
              onRemoved={(id) => setList((l) => l.filter((x) => x.id !== id))} />
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 98px', gap: 10, alignItems: 'center', padding: '10px 0' }}>
            <input placeholder="ABBR." value={newAbbr} onChange={(e) => setNewAbbr(e.target.value)} style={{ ...input, fontFamily: MONO }} />
            <input placeholder="Meaning" value={newMeaning} onChange={(e) => setNewMeaning(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add() }} style={input} />
            <button onClick={add} disabled={!newAbbr.trim() || !newMeaning.trim()}
              style={{ fontFamily: FONT, fontSize: 12, padding: '6px 12px', borderRadius: 20, border: '0.5px solid #07272D', background: '#07272D', color: '#fff', cursor: 'pointer', opacity: newAbbr.trim() && newMeaning.trim() ? 1 : 0.4 }}>
              + Add
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
